import { FC, useEffect } from 'react';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { IconButton, Typography } from '@mui/material';
import { MoreVert } from '@mui/icons-material';
import { motion } from 'framer-motion';
import clsx from 'clsx';
import { useNavigate } from 'react-router-dom';

import './ChatSideBar.scss';

import {
  assistantChatProcessedIdsAtom,
  settingsSidebarStateAtom,
  useAssistantChat,
} from '../../../data-access';
import { Path } from '../../../routing';
import { AssistantChatContent } from '../AssistantChatContent';
import { AssistantChatScheduler } from '../AssistantChatScheduler';

interface ChatSideBarProps {
  className?: string;
}

const ChatSideBar: FC<ChatSideBarProps> = ({ className }) => {
  const [processedIds, setProcessedIds] = useRecoilState(assistantChatProcessedIdsAtom);
  const setSettingsSidebarState = useSetRecoilState(settingsSidebarStateAtom);
  const navigate = useNavigate();
  const {
    openAssistantChat,
    setOpenAssistantChat,
    messages,
    startPlanChat,
    isSchedulerMode,
    clearMessages,
  } = useAssistantChat();
  useEffect(() => {
    if (!openAssistantChat) {
      return;
    }
    if (messages.length === 0 && !isSchedulerMode) {
      startPlanChat();
    }
  }, [isSchedulerMode, messages.length, openAssistantChat, startPlanChat]);

  const handleOpenSettings = () => {
    setSettingsSidebarState({
      selectedParentKey: 'aiAssistant',
      expandedParentKeys: [],
    });
    navigate(`${Path.HOME}/settings`);
    setOpenAssistantChat(false);
  };

  return (
    <motion.div
      className={clsx('chat-side-bar', className)}
      initial={{ opacity: 0, x: -8 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -8 }}
      transition={{ duration: 0.22, ease: 'easeOut' }}
    >
      <div className="chat-side-bar__content">
        {isSchedulerMode ? (
          <AssistantChatScheduler
            onClear={clearMessages}
            setProcessedIds={setProcessedIds}
          />
        ) : (
          <AssistantChatContent
            processedIds={processedIds}
            setProcessedIds={setProcessedIds}
            onClear={clearMessages}
            hideHeader
            quickActionsPlacement="above-input"
            className="assistant-chat-content-container--sidebar-compact"
          />
        )}
      </div>
    </motion.div>
  );
};

export default ChatSideBar;
